import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BookOpen, History, Plus, ImagePlus, Cake } from "lucide-react";
import { toast } from "sonner";
import { ptBR } from "date-fns/locale";
import { wapi, API } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";

const inputCls =
  "w-full min-h-[48px] px-4 bg-secondary/40 rounded-md border border-border text-base outline-none focus:border-primary transition-colors";

const fmtDay = (iso) => {
  const s = new Date(iso + "T12:00:00").toLocaleDateString("pt-BR", {
    weekday: "long", day: "2-digit", month: "long", year: "numeric",
  });
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const fmtShort = (iso) =>
  new Date(iso + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });

const STATUS_LABEL = { open: "Precisa de responsável", assigned: "Confirmado", done: "Realizado", cancelled: "Desativado" };
const STATUS_CHIP = {
  open: "border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  assigned: "border-primary/40 bg-primary/10 text-primary",
  done: "border-border bg-secondary/60 text-muted-foreground",
  cancelled: "border-border bg-secondary/60 text-muted-foreground",
};

const SERIES_NAME = {
  terca: "Estudo do Evangelho e do Livro dos Espíritos",
  quinta: "Estudo da Mediunidade",
  oracao: "Reunião de Orações e Irradiação",
  mediunica: "Reunião Mediúnica · fechada",
  estudo: "Estudo (extra)",
  palestra: "Palestra",
  outro: "Evento",
  aniversario: "Aniversário da equipe",
};
const SERIES_SHORT = { terca: "Terça-feira", quinta: "Quinta-feira", oracao: "1º sábado", mediunica: "Quinta (mediúnica)", estudo: "Estudo", palestra: "Palestra", outro: "Evento", aniversario: "Aniversário" };
const KIND_OPTIONS = [
  ["palestra", "Palestra"],
  ["estudo", "Estudo"],
  ["mediunica", "Reunião mediúnica"],
  ["aniversario", "Aniversário"],
  ["outro", "Outro"],
];

const STOPPED_PLACEHOLDER = {
  terca: "Onde parou (ex.: Evangelho cap. XIII, item 17 · Livro dos Espíritos, questão 123)",
  quinta: "Onde o estudo parou (ex.: capítulo ou item do estudo da mediunidade)",
};

const toDate = (iso) => new Date(iso + "T12:00:00");
const isoOf = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// membro que ainda não fez login não tem nome — exibe a parte local do e-mail formatada
const disp = (n, e) => {
  if (n && !n.includes("@")) return n;
  const email = e || n || "";
  const local = email.split("@")[0].replace(/[._-]+/g, " ").trim();
  return local ? local.replace(/\b\w/g, (c) => c.toUpperCase()) : email;
};

const SessionCard = ({ s, user, team, todayISO, onAction }) => {
  const mine = s.assignee_email && s.assignee_email === user.email;
  const isPastOrToday = s.date <= todayISO;
  const isPast = s.date < todayISO;
  const isStudy = s.series === "terca" || s.series === "quinta" || s.series === "estudo";
  const isMediunica = s.series === "mediunica";
  const isAniversario = s.series === "aniversario";
  // upload de imagem/flyer/PDF liberado para todos os eventos — menos aniversário (ocorrência virtual, informativa)
  const canHaveFlyer = !isAniversario;
  const isPdfFlyer = (s.flyer_name || "").toLowerCase().endsWith(".pdf");
  // publicação no site: só eventos avulsos; mediúnica e aniversário são internos
  const canPublish = !!s.custom && !isMediunica && !isAniversario && s.status !== "cancelled";
  // formato (presencial/online/híbrido): eventos avulsos publicáveis, admin ou quem criou
  const canFormat = !!s.custom && ["estudo", "palestra", "outro"].includes(s.series)
    && (user.is_admin || s.created_by === user.email);
  const FMT_LABEL = { onsite: "Presencial", online: "Online", hybrid: "Presencial + online" };
  // qualquer trabalhador pode registrar um encontro que já aconteceu (muitos anotam por colegas)
  const canRegister = !isAniversario && (
    (s.status === "open" && isPastOrToday) ||
    (s.status === "assigned" && (mine || user.is_admin || isPastOrToday)));
  // quem registrou (ou admin) pode corrigir um registro já feito
  const canEditDone = s.status === "done" && (user.is_admin || s.registered_by_email === user.email);
  // eventos avulsos podem ser excluídos de vez (admin ou quem criou); fixos só desativam
  const canDelete = !!s.custom && !isAniversario && (user.is_admin || s.created_by === user.email);
  // passado sem registro não pede mais responsável — é só "sem registro"
  const statusLabel = isAniversario
    ? "Aniversário"
    : s.status === "open" && isPast
      ? "Sem registro"
      : isMediunica && s.status === "open" ? "Aguardando confirmações" : STATUS_LABEL[s.status];
  const statusChip = isAniversario || (s.status === "open" && isPast) ? STATUS_CHIP.cancelled : STATUS_CHIP[s.status];

  const [confirm, setConfirm] = useState(""); // "release" | "cancel"
  const [fmtOpen, setFmtOpen] = useState(false);
  const [fmtVal, setFmtVal] = useState("onsite");
  const [fmtLink, setFmtLink] = useState("");
  const [topicOpen, setTopicOpen] = useState(false);
  const [topic, setTopic] = useState(s.topic || "");
  const [doneOpen, setDoneOpen] = useState(false);
  const [doneForm, setDoneForm] = useState({
    studied_topic: s.topic || "",
    stopped_at: "",
    note_for_next: "",
    led_by_email: s.assignee_email || user.email,
  });
  const [cancelReason, setCancelReason] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignEmail, setAssignEmail] = useState("");
  const [assignOther, setAssignOther] = useState("");
  const [ledByOther, setLedByOther] = useState("");
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const uploadFlyer = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      await wapi.post(`/workers/studies/${s.session_id}/flyer`, fd);
      toast.success("Flyer adicionado.");
      onAction("reload");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar a imagem.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const openEditDone = () => {
    setDoneForm({
      studied_topic: s.studied_topic || "",
      stopped_at: s.stopped_at || "",
      note_for_next: s.note_for_next || "",
      led_by_email: s.led_by_email || (s.led_by_name ? "__outro" : user.email),
    });
    setLedByOther(s.led_by_email ? "" : s.led_by_name || "");
    setDoneOpen(true);
  };

  const ledByOptions = useMemo(() => {
    const opts = [...team];
    if (s.assignee_email && !opts.some((t) => t.email === s.assignee_email)) {
      opts.unshift({ email: s.assignee_email, name: s.assignee_name || s.assignee_email });
    }
    if (!opts.some((t) => t.email === user.email)) {
      opts.push({ email: user.email, name: user.name || user.email });
    }
    return opts;
  }, [team, s.assignee_email, s.assignee_name, user.email, user.name]);

  const submitDone = async () => {
    if (isStudy && !doneForm.stopped_at.trim()) {
      toast.error("Informe onde o estudo parou.");
      return;
    }
    let ok;
    if (doneForm.led_by_email === "__outro") {
      if (!ledByOther.trim()) {
        toast.error("Informe quem conduziu.");
        return;
      }
      ok = await onAction("complete", s, { ...doneForm, led_by_email: "", led_by_name: ledByOther.trim() });
    } else {
      const t = ledByOptions.find((o) => o.email === doneForm.led_by_email);
      ok = await onAction("complete", s, { ...doneForm, led_by_name: t ? disp(t.name, t.email) : "" });
    }
    if (ok) {
      setDoneOpen(false);
      setLedByOther("");
    }
  };

  const submitAssign = async () => {
    let ok = false;
    if (assignEmail === "__outro") {
      if (!assignOther.trim()) {
        toast.error("Digite o nome do responsável.");
        return;
      }
      ok = await onAction("assign", s, { name: assignOther.trim() });
    } else if (assignEmail) {
      ok = await onAction("assign", s, { email: assignEmail });
    } else {
      toast.error("Escolha o responsável.");
      return;
    }
    if (ok) {
      setAssignOpen(false);
      setAssignEmail("");
      setAssignOther("");
    }
  };

  const ghost = "rounded-full border border-border px-4 py-2 font-sans text-sm text-muted-foreground hover:text-foreground transition-colors";
  const primary = "rounded-full bg-primary px-5 py-3 font-sans text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors";

  const deactivateBtn = (
    <button onClick={() => setConfirm("cancel")} className={ghost} data-testid="study-cancel">Desativar data</button>
  );
  const deactivateConfirm = (
    <span className="inline-flex items-center gap-2 flex-wrap">
      <input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Motivo (opcional)" className="min-h-[40px] px-3 bg-secondary/40 rounded-md border border-border text-sm outline-none" data-testid="study-cancel-reason" />
      <button onClick={() => onAction("cancel", s, cancelReason)} className="rounded-full bg-red-600 px-4 py-2 font-sans text-sm font-semibold text-white" data-testid="study-cancel-yes">Sim, desativar</button>
      <button onClick={() => setConfirm("")} className={ghost}>Voltar</button>
    </span>
  );

  const canAssign = user.is_admin && !isMediunica && !isAniversario && !isPast && (s.status === "open" || s.status === "assigned");
  const assignUI = (
    <>
      <button onClick={() => setAssignOpen((v) => !v)} className={ghost} data-testid="study-assign-toggle">
        {s.status === "assigned" ? "Trocar responsável" : "Atribuir a alguém"}
      </button>
      {assignOpen && (
        <span className="inline-flex items-center gap-2 flex-wrap w-full" data-testid="study-assign-form">
          <select value={assignEmail} onChange={(e) => setAssignEmail(e.target.value)} className="min-h-[40px] px-3 bg-secondary/40 rounded-md border border-border text-sm outline-none flex-1 min-w-[200px]" data-testid="study-assign-select">
            <option value="">Escolha o responsável…</option>
            {team.map((t) => <option key={t.email} value={t.email}>{disp(t.name, t.email)}</option>)}
            <option value="__outro">Outro (digitar nome)…</option>
          </select>
          {assignEmail === "__outro" && (
            <input value={assignOther} onChange={(e) => setAssignOther(e.target.value)} placeholder="Nome do responsável" className="min-h-[40px] px-3 bg-secondary/40 rounded-md border border-border text-sm outline-none flex-1 min-w-[200px]" data-testid="study-assign-other" />
          )}
          <button
            onClick={submitAssign}
            className="rounded-full bg-primary px-4 py-2 font-sans text-sm font-semibold text-primary-foreground"
            data-testid="study-assign-save"
          >
            Confirmar
          </button>
        </span>
      )}
    </>
  );

  return (
    <li className="rounded-xl border border-border bg-card p-5" data-testid={`study-${s.session_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="font-sans text-[11px] uppercase tracking-[0.15em] text-muted-foreground" data-testid="study-series">
            {SERIES_NAME[s.series] || s.series}
          </p>
          {s.title && <p className="mt-0.5 font-sans text-sm font-medium text-foreground" data-testid="study-title">{s.title}</p>}
          <p className="mt-1 font-serif text-lg md:text-xl text-foreground">{fmtDay(s.date)}</p>
          {s.time && <p className="font-sans text-sm text-muted-foreground">{s.time}h (horário de Berna)</p>}
          {isAniversario && s.turning_age > 0 && (
            <p className="font-sans text-sm text-muted-foreground" data-testid="bday-age">faz {s.turning_age} anos</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-block rounded-full border px-3 py-1 font-sans text-xs font-semibold ${statusChip}`} data-testid="study-status">
            {statusLabel}
          </span>
          {s.published && (
            <span className="inline-block rounded-full border border-primary/40 bg-primary/10 px-3 py-1 font-sans text-xs font-semibold text-primary" data-testid="study-published-chip">
              No site
            </span>
          )}
        </div>
      </div>

      {s.status === "cancelled" && (
        <p className="mt-3 font-sans text-sm text-muted-foreground">
          {s.cancel_reason ? `Motivo: ${s.cancel_reason}` : "Data desativada."}
        </p>
      )}

      {isPast && (s.status === "open" || s.status === "assigned") && !isAniversario && (
        <p className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 font-sans text-xs font-semibold text-amber-700 dark:text-amber-300" data-testid="study-past-pending">
          <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-amber-500 text-[9px] font-bold leading-none text-white">!</span>
          {s.status === "open" ? "Encontro passado · registro pendente" : "Encontro passado · confirmação não concluída"}
        </p>
      )}

      {s.status === "assigned" && (
        <div className="mt-3 space-y-1">
          <p className="font-sans text-base" data-testid="study-assignee">
            {isMediunica ? "Confirmado por" : "Responsável"}: <strong>{disp(s.assignee_name, s.assignee_email)}</strong>
          </p>
          {s.topic && <p className="font-sans text-base text-muted-foreground" data-testid="study-topic">Tema previsto: {s.topic}</p>}
        </div>
      )}

      {s.status === "done" && (
        <div className="mt-3 space-y-1" data-testid="study-done-info">
          <p className="font-sans text-base" data-testid="study-led-by">
            Realizado por <strong>{disp(s.led_by_name || s.assignee_name, s.led_by_email || s.assignee_email) || "—"}</strong>
          </p>
          {s.studied_topic && <p className="font-sans text-base text-muted-foreground" data-testid="study-done-topic-text">Tema: {s.studied_topic}</p>}
          {s.stopped_at && <p className="font-sans text-base text-muted-foreground" data-testid="study-stopped-text">Último ponto estudado: <strong>{s.stopped_at}</strong></p>}
          {s.note_for_next && <p className="font-sans text-sm italic text-muted-foreground">“{s.note_for_next}”</p>}
          {s.registered_by_name && s.registered_by_name !== (s.led_by_name || s.assignee_name) && (
            <p className="font-sans text-xs text-muted-foreground">anotado por {s.registered_by_name}</p>
          )}
        </div>
      )}

      {s.continuity && s.status !== "cancelled" && (
        <div className="mt-4 rounded-lg border border-primary/25 bg-primary/5 p-4" data-testid="study-continuity">
          <p className="font-sans text-[11px] uppercase tracking-[0.15em] font-semibold text-primary">
            Continuação do último encontro ({fmtShort(s.continuity.date)} · {disp(s.continuity.led_by_name || s.continuity.assignee_name, "")})
          </p>
          <p className="mt-2 font-sans text-sm leading-relaxed">{s.continuity.studied_topic}</p>
          <p className="font-sans text-sm leading-relaxed">Último ponto estudado: <strong>{s.continuity.stopped_at}</strong></p>
          {s.continuity.note_for_next && (
            <p className="mt-1 font-sans text-sm italic text-muted-foreground">“{s.continuity.note_for_next}”</p>
          )}
        </div>
      )}

      {canHaveFlyer && (
        <div className="mt-4" data-testid="flyer-block">
          {s.flyer_path ? (
            <div className="flex items-start gap-3 flex-wrap">
              {isPdfFlyer ? (
                <a href={`${API}/files/${s.flyer_path}`} target="_blank" rel="noopener noreferrer" data-testid="flyer-link"
                  className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2.5 font-sans text-sm text-primary hover:bg-primary/10 transition-colors">
                  <ImagePlus size={14} strokeWidth={1.5} /> {s.flyer_name || "Anexo (PDF)"}
                </a>
              ) : (
                <a href={`${API}/files/${s.flyer_path}`} target="_blank" rel="noopener noreferrer" data-testid="flyer-link">
                  <img src={`${API}/files/${s.flyer_path}`} alt={s.title || "Flyer do evento"} className="max-h-32 w-auto rounded-lg border border-border object-cover" data-testid="flyer-img" />
                </a>
              )}
              <button onClick={() => fileRef.current?.click()} className={ghost} data-testid="flyer-toggle" disabled={uploading}>
                {uploading ? "Enviando…" : "Trocar anexo"}
              </button>
            </div>
          ) : (
            <button onClick={() => fileRef.current?.click()} className={ghost} data-testid="flyer-toggle" disabled={uploading}>
              <ImagePlus size={14} strokeWidth={1.5} className="inline-block mr-1.5 -mt-0.5" />
              {uploading ? "Enviando…" : "Adicionar imagem/flyer (JPG, PNG, WebP, PDF)"}
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={uploadFlyer} data-testid="flyer-input" />
        </div>
      )}

      {/* ações */}
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {s.status === "open" && (
          <>
            {!isAniversario && !isPast && (!isMediunica ? (
              <button onClick={() => onAction("claim", s)} className={primary + " min-h-[48px]"} data-testid="study-claim">
                {isStudy ? "Eu faço este estudo" : "Eu fico responsável"}
              </button>
            ) : (
              <button onClick={() => onAction("claim", s)} className={primary + " min-h-[48px]"} data-testid="study-claim">
                Confirmar data
              </button>
            ))}
            {!isAniversario && (confirm === "cancel" ? deactivateConfirm : deactivateBtn)}
            {canAssign && assignUI}
          </>
        )}
        {s.status === "cancelled" && (
          <button onClick={() => onAction("reactivate", s)} className={ghost} data-testid="study-reactivate">Ativar data</button>
        )}
        {canRegister && (
          <button onClick={() => { setDoneOpen((v) => !v); setTopicOpen(false); }} className="rounded-full border border-primary/40 px-4 py-2 font-sans text-sm text-primary hover:bg-primary/10 transition-colors" data-testid="study-done-toggle">
            {isStudy ? "Registrar estudo realizado" : "Registrar encontro realizado"}
          </button>
        )}
        {canEditDone && (
          <>
            <button onClick={openEditDone} className={ghost} data-testid="study-edit-toggle">Editar registro</button>
            {confirm === "undone" ? (
              <span className="inline-flex items-center gap-2 flex-wrap" data-testid="study-undone-confirm">
                <span className="font-sans text-sm text-muted-foreground">Desfazer o registro? O encontro volta a ficar pendente.</span>
                <button onClick={() => onAction("undone", s)} className="rounded-full bg-amber-600 px-4 py-2 font-sans text-sm font-semibold text-white" data-testid="study-undone-yes">Sim, desfazer</button>
                <button onClick={() => setConfirm("")} className={ghost}>Voltar</button>
              </span>
            ) : (
              <button onClick={() => setConfirm("undone")} className={ghost} data-testid="study-undone">Desfazer registro</button>
            )}
          </>
        )}
        {s.status === "assigned" && (mine || user.is_admin) && (
          <>
            {isStudy && (
              <button onClick={() => { setTopicOpen((v) => !v); setDoneOpen(false); }} className={ghost} data-testid="study-topic-toggle">
                {s.topic ? "Ajustar tema" : "Definir tema"}
              </button>
            )}
            {confirm === "release" ? (
              <span className="inline-flex items-center gap-2" data-testid="study-release-confirm">
                <span className="font-sans text-sm text-muted-foreground">{isMediunica ? "Desfazer a confirmação?" : "Liberar a data?"}</span>
                <button onClick={() => onAction("release", s)} className="rounded-full bg-amber-600 px-4 py-2 font-sans text-sm font-semibold text-white" data-testid="study-release-yes">Sim</button>
                <button onClick={() => setConfirm("")} className={ghost}>Não</button>
              </span>
            ) : (
              <button onClick={() => setConfirm("release")} className={ghost} data-testid="study-release">{isMediunica ? "Desfazer confirmação" : "Liberar data"}</button>
            )}
            {confirm === "cancel" ? deactivateConfirm : deactivateBtn}
            {canAssign && assignUI}
          </>
        )}
        {canPublish && (
          <button onClick={() => onAction("publish", s, !s.published)} className={ghost} data-testid="study-publish-toggle">
            {s.published ? "Despublicar do site" : "Publicar no site"}
          </button>
        )}
        {canFormat && (
          fmtOpen ? (
            <span className="inline-flex items-center gap-2 flex-wrap" data-testid="format-form">
              <select value={fmtVal} onChange={(e) => setFmtVal(e.target.value)} className="min-h-[40px] px-3 bg-secondary/40 rounded-md border border-border text-sm" data-testid="format-select">
                <option value="onsite">Presencial</option>
                <option value="online">Online</option>
                <option value="hybrid">Presencial + online</option>
              </select>
              {fmtVal !== "onsite" && (
                <input value={fmtLink} onChange={(e) => setFmtLink(e.target.value)} placeholder="Link https://… (opcional)" className="min-h-[40px] px-3 bg-secondary/40 rounded-md border border-border text-sm min-w-[200px]" data-testid="format-link" />
              )}
              <button onClick={() => { onAction("format", s, { fmt: fmtVal, online_link: fmtLink }); setFmtOpen(false); }} className="rounded-full bg-primary px-4 py-2 font-sans text-sm font-semibold text-primary-foreground" data-testid="format-save">Salvar</button>
              <button onClick={() => setFmtOpen(false)} className="rounded-full border border-border px-4 py-2 font-sans text-sm text-muted-foreground hover:text-foreground transition-colors">Voltar</button>
            </span>
          ) : (
            <button onClick={() => { setFmtVal(s.fmt || "onsite"); setFmtLink(s.online_link || ""); setFmtOpen(true); }} className={ghost} data-testid="format-toggle">
              Formato: {FMT_LABEL[s.fmt || "onsite"]}
            </button>
          )
        )}
        {canDelete && (
          confirm === "delete" ? (
            <span className="inline-flex items-center gap-2 flex-wrap" data-testid="study-delete-confirm">
              <span className="font-sans text-sm text-muted-foreground">Excluir este evento de vez? Não dá para recuperar.</span>
              <button onClick={() => onAction("delete", s)} className="rounded-full bg-red-600 px-4 py-2 font-sans text-sm font-semibold text-white" data-testid="study-delete-yes">Sim, excluir</button>
              <button onClick={() => setConfirm("")} className={ghost}>Voltar</button>
            </span>
          ) : (
            <button onClick={() => setConfirm("delete")} className={ghost} data-testid="study-delete">Excluir evento</button>
          )
        )}
        {isAniversario && s.session_id.startsWith("bday-") && (user.is_admin || s.created_by === user.email) && (
          confirm === "delbday" ? (
            <span className="inline-flex items-center gap-2 flex-wrap" data-testid="bday-delete-confirm">
              <span className="font-sans text-sm text-muted-foreground">Excluir de todos os anos?</span>
              <button onClick={() => onAction("delbday", s, s.session_id.split("-")[1])} className="rounded-full bg-red-600 px-4 py-2 font-sans text-sm font-semibold text-white" data-testid="bday-delete-yes">Sim, excluir</button>
              <button onClick={() => setConfirm("")} className={ghost}>Voltar</button>
            </span>
          ) : (
            <button onClick={() => setConfirm("delbday")} className={ghost} data-testid="bday-delete">Excluir aniversário</button>
          )
        )}
      </div>

      {topicOpen && (
        <div className="mt-4 flex gap-2 flex-wrap" data-testid="study-topic-form">
          <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Tema do encontro…" className={inputCls + " flex-1 min-w-[220px]"} data-testid="study-topic-input" />
          <button onClick={() => onAction("topic", s, topic)} className={primary} data-testid="study-topic-save">Salvar tema</button>
        </div>
      )}

      {doneOpen && (
        <div className="mt-4 rounded-lg border border-border bg-secondary/30 p-4 space-y-3" data-testid="study-done-form">
          <p className="font-sans text-sm font-medium">
            {isStudy ? "Como foi o estudo? Isso orienta o próximo responsável." : "Como foi o encontro? (opcional)"}
          </p>
          <label className="block">
            <span className="font-sans text-xs uppercase tracking-[0.12em] text-muted-foreground">Conduzido por</span>
            <select
              value={doneForm.led_by_email}
              onChange={(e) => setDoneForm({ ...doneForm, led_by_email: e.target.value })}
              className={inputCls + " mt-1"}
              data-testid="study-done-ledby"
            >
              {ledByOptions.map((t) => (
                <option key={t.email} value={t.email}>{disp(t.name, t.email)}</option>
              ))}
              <option value="__outro">Outro (digitar nome)…</option>
            </select>
          </label>
          {doneForm.led_by_email === "__outro" && (
            <input value={ledByOther} onChange={(e) => setLedByOther(e.target.value)} placeholder="Nome de quem conduziu (não está no sistema)" className={inputCls} data-testid="study-done-ledby-other" />
          )}
          {isStudy && (
            <>
              <input value={doneForm.studied_topic} onChange={(e) => setDoneForm({ ...doneForm, studied_topic: e.target.value })} placeholder="Tema estudado (ex.: Evangelho – Capítulo XIII)" className={inputCls} data-testid="study-done-topic" />
              <input value={doneForm.stopped_at} onChange={(e) => setDoneForm({ ...doneForm, stopped_at: e.target.value })} placeholder={STOPPED_PLACEHOLDER[s.series] || "Onde o estudo parou"} className={inputCls} data-testid="study-done-stopped" />
            </>
          )}
          <textarea value={doneForm.note_for_next} onChange={(e) => setDoneForm({ ...doneForm, note_for_next: e.target.value })} rows={2} placeholder={isStudy ? "Observação para o próximo trabalhador (opcional)" : "Anotações do encontro (opcional)"} className={inputCls + " py-3 resize-y"} data-testid="study-done-note" />
          <button onClick={submitDone} className={primary + " w-full min-h-[48px]"} data-testid="study-done-save">
            {isStudy ? "Salvar registro do estudo" : "Salvar registro do encontro"}
          </button>
        </div>
      )}
    </li>
  );
};

export const StudiesBoard = ({ user }) => {
  const [data, setData] = useState({ upcoming: [], history: [] });
  const [team, setTeam] = useState([]);
  const [histOpen, setHistOpen] = useState(false);
  const [bdayOpen, setBdayOpen] = useState(false);
  const [bdayList, setBdayList] = useState([]);
  const [bdayConfirm, setBdayConfirm] = useState("");
  const [histFilter, setHistFilter] = useState({ series: "", q: "" });
  const [pastSel, setPastSel] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [month, setMonth] = useState(null);
  const [until, setUntil] = useState(""); // até onde a escala já foi gerada (navegação infinita)
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ date: "", kind: "palestra", title: "", time: "", fmt: "onsite", online_link: "" });

  const todayISO = useMemo(
    () => new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Zurich" }).format(new Date()),
    []
  );

  const load = useCallback((untilParam, opts = {}) => {
    const eff = untilParam !== undefined ? untilParam : until;
    const params = [];
    if (eff) params.push(`until=${eff}`);
    if (opts.start) params.push(`start=${opts.start}`);
    const q = params.length ? `?${params.join("&")}` : "";
    return wapi.get(`/workers/studies${q}`).then((r) => {
      if (opts.merge) {
        // mescla janelas (meses passados navegados) sem perder o que já estava carregado
        setData((prev) => {
          const map = new Map(prev.upcoming.map((s) => [s.session_id, s]));
          r.data.upcoming.forEach((s) => map.set(s.session_id, s));
          return { ...prev, upcoming: [...map.values()] };
        });
      } else {
        setData(r.data);
      }
    }).catch(() => toast.error("Não foi possível carregar a escala."));
  }, [until]);
  useEffect(() => { load(""); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    wapi.get("/workers/names").then((r) => setTeam(r.data.workers || [])).catch(() => {});
  }, []);

  const allSessions = useMemo(() => [...data.upcoming, ...data.history], [data]);
  // um dia pode ter MAIS de um encontro (quinta = mediúnica 18h + estudo 19h)
  const byDate = useMemo(() => {
    const m = {};
    for (const s of allSessions) (m[s.date] = m[s.date] || []).push(s);
    for (const k in m) m[k].sort((a, b) => a.time.localeCompare(b.time));
    return m;
  }, [allSessions]);

  // seleção inicial: próximo encontro não desativado
  useEffect(() => {
    if (selectedDate || data.upcoming.length === 0) return;
    const next = data.upcoming.find((s) => s.status !== "cancelled") || data.upcoming[0];
    if (next) {
      setSelectedDate(next.date);
      setMonth(toDate(next.date));
    }
  }, [data, selectedDate]);

  const selectedList = selectedDate ? byDate[selectedDate] || [] : [];

  // CONDIÇÃO TEMPORAL (passado/hoje/futuro) é derivada da data em Europe/Zurich —
  // nunca altera o status administrativo persistido (open/assigned/done/cancelled).
  // Passado: verde suave; com pendência administrativa (open=registro pendente,
  // assigned=confirmado mas sem registro de realizado) → mesmo verde + indicador "!".
  // Desativado prevalece sobre tudo, inclusive no passado. Aniversários são informativos.
  const { modifiers, dayHints } = useMemo(() => {
    const by = { open: [], partial: [], confirmed: [], done: [], deactivated: [], past: [], pastPending: [], info: [] };
    const hints = {};
    for (const [date, sessions] of Object.entries(byDate)) {
      const eff = sessions.filter((s) => s.series !== "aniversario");
      const d = toDate(date);
      if (eff.length === 0) {
        by.info.push(d);
        hints[date] = "Aniversário da equipe";
        continue;
      }
      const sts = eff.map((s) => s.status);
      if (sts.every((t) => t === "cancelled")) {
        by.deactivated.push(d);
        hints[date] = "Desativado";
        continue;
      }
      if (date < todayISO) {
        const hasPending = sts.some((t) => t === "open" || t === "assigned");
        if (hasPending) {
          by.pastPending.push(d);
          hints[date] = sts.includes("open")
            ? "Encontro passado · registro pendente"
            : "Encontro passado · confirmação não concluída";
        } else {
          by.past.push(d);
          hints[date] = "Passado";
        }
        continue;
      }
      const hasOpen = sts.includes("open");
      if (hasOpen && sts.every((t) => t === "open")) {
        by.open.push(d);
        hints[date] = "Pendente — precisa de responsável";
      } else if (hasOpen) {
        by.partial.push(d);
        hints[date] = "Pendência parcial";
      } else if (sts.includes("assigned")) {
        by.confirmed.push(d);
        hints[date] = "Confirmado";
      } else if (sts.includes("done")) {
        by.done.push(d);
        hints[date] = "Realizado";
      } else {
        by.deactivated.push(d);
        hints[date] = "Desativado";
      }
    }
    return { modifiers: by, dayHints: hints };
  }, [byDate, todayISO]);

  // conteúdo do dia com acessibilidade: tooltip (desktop) + "!" discreto em passado com pendência
  const DayContent = useCallback(({ date: d, activeModifiers }) => {
    const hint = dayHints[isoOf(d)];
    return (
      <span title={hint} aria-label={hint} className="relative inline-flex h-full w-full items-center justify-center">
        {d.getDate()}
        {activeModifiers?.pastPending && (
          <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-amber-500 text-[9px] font-bold leading-none text-white" data-testid="past-pending-dot">!</span>
        )}
      </span>
    );
  }, [dayHints]);

  // navegação infinita: ao passar do horizonte carregado, gera mais meses no backend;
  // meses passados carregam a janela do mês (o histórico não inclui aniversários)
  const handleMonthChange = useCallback((m) => {
    setMonth(m);
    const need = isoOf(new Date(m.getFullYear(), m.getMonth() + 1, 0));
    if (need < todayISO) {
      const monthStart = isoOf(new Date(m.getFullYear(), m.getMonth(), 1));
      load(need, { start: monthStart, merge: true });
      return;
    }
    const baseLimit = isoOf(new Date(Date.now() + 90 * 86400000));
    const current = until && until > baseLimit ? until : baseLimit;
    if (need > current) {
      setUntil(need);
      load(need);
    }
  }, [until, load, todayISO]);

  const onAction = async (kind, s, payload) => {
    try {
      if (kind === "reload") { load(); return; }
      if (kind === "claim") await wapi.post(`/workers/studies/${s.session_id}/claim`);
      if (kind === "release") await wapi.post(`/workers/studies/${s.session_id}/release`);
      if (kind === "reactivate") await wapi.post(`/workers/studies/${s.session_id}/reactivate`);
      if (kind === "cancel") await wapi.post(`/workers/studies/${s.session_id}/cancel`, { reason: payload || "" });
      if (kind === "topic") await wapi.patch(`/workers/studies/${s.session_id}/topic`, { topic: payload });
      if (kind === "complete") await wapi.post(`/workers/studies/${s.session_id}/complete`, payload);
      if (kind === "assign") await wapi.post(`/workers/studies/${s.session_id}/assign`, payload);
      if (kind === "publish") await wapi.post(`/workers/studies/${s.session_id}/publish`, { published: payload });
      if (kind === "custom") await wapi.post(`/workers/studies/custom`, payload);
      if (kind === "birthday") {
        const r = await wapi.post(`/workers/birthdays`, payload);
        payload = { ...payload, date: r.data.next_date }; // seleciona a próxima ocorrência no calendário
      }
      if (kind === "delbday") await wapi.delete(`/workers/birthdays/${payload}`);
      if (kind === "undone") await wapi.post(`/workers/studies/${s.session_id}/undone`);
      if (kind === "format") await wapi.post(`/workers/studies/${s.session_id}/format`, payload);
      if (kind === "delete") await wapi.delete(`/workers/studies/${s.session_id}`);
      const med = s?.series === "mediunica";
      const msgs = {
        claim: med ? "Data confirmada. Obrigado!" : "Você assumiu este encontro. Obrigado!",
        release: med ? "Confirmação desfeita." : "Data liberada para a equipe.",
        reactivate: "Data ativada.", cancel: "Data desativada.", topic: "Tema salvo.",
        complete: "Registro salvo. Obrigado!",
        assign: "Data atribuída.",
        publish: payload ? "Evento publicado no site." : "Evento despublicado do site.",
        custom: "Evento adicionado.",
        birthday: "Aniversário adicionado — aparece todo ano no calendário.",
        delbday: "Aniversário excluído.",
        undone: "Registro desfeito — o encontro voltou ao normal.",
        format: "Formato salvo.",
        delete: "Evento excluído.",
      };
      toast.success(msgs[kind]);
      if (kind === "complete") setPastSel("");
      if (kind === "custom" || kind === "birthday") {        // garante que a data do evento esteja carregada e a seleciona no calendário
        const d = toDate(payload.date);
        const need = isoOf(new Date(d.getFullYear(), d.getMonth() + 1, 0));
        const baseLimit = isoOf(new Date(Date.now() + 90 * 86400000));
        const current = until && until > baseLimit ? until : baseLimit;
        if (need > current) {
          setUntil(need);
          await load(need);
        } else {
          await load();
        }
        setSelectedDate(payload.date);
        setMonth(d);
      } else {
        load();
      }
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível concluir a ação.");
      load(); // reconcilia (ex.: outra pessoa assumiu primeiro)
      return false;
    }
  };

  const submitCustom = () => {
    if (!addForm.date) {
      toast.error(addForm.kind === "aniversario" ? "Escolha a data de nascimento." : "Escolha a data.");
      return;
    }
    if (["palestra", "outro", "aniversario"].includes(addForm.kind) && !addForm.title.trim()) {
      toast.error(addForm.kind === "aniversario" ? "Informe o nome do aniversariante." : "Informe o tema/título do evento.");
      return;
    }
    if (addForm.kind === "aniversario") {
      onAction("birthday", null, { name: addForm.title.trim(), birth_date: addForm.date });
    } else {
      onAction("custom", null, { ...addForm, title: addForm.title.trim(), online_link: addForm.online_link.trim() });
    }
    setAddForm({ date: "", kind: "palestra", title: "", time: "", fmt: "onsite", online_link: "" });
    setAddOpen(false);
  };

  const toggleBdays = async () => {
    if (!bdayOpen) {
      try {
        const { data } = await wapi.get("/workers/birthdays");
        setBdayList(data.birthdays);
      } catch {
        toast.error("Não foi possível carregar os aniversariantes.");
        return;
      }
    }
    setBdayOpen((v) => !v);
  };

  const delBdayFromList = async (bid) => {
    try {
      await wapi.delete(`/workers/birthdays/${bid}`);
      toast.success("Aniversário excluído.");
      setBdayConfirm("");
      setBdayList((prev) => prev.filter((b) => b.birthday_id !== bid));
      load("", {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível excluir.");
    }
  };

  const twoWeeks = useMemo(() => {
    // só o que AINDA pode ser resolvido: hoje/futuro (passado não entra no alerta)
    const limit = isoOf(new Date(Date.now() + 14 * 86400000));
    return data.upcoming.filter((s) => s.series !== "mediunica" && s.series !== "aniversario"
      && s.status === "open" && s.date >= todayISO && s.date <= limit).length;
  }, [data.upcoming, todayISO]);

  const history = useMemo(() => {
    return data.history.filter((h) => {
      if (histFilter.series && h.series !== histFilter.series) return false;
      if (histFilter.q) {
        const q = histFilter.q.toLowerCase();
        const hay = [h.studied_topic, h.topic, h.stopped_at, h.note_for_next, h.assignee_name, h.led_by_name, h.title].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data.history, histFilter]);

  // datas passadas (terças/quintas, últimos 60 dias) que ainda não existem na escala —
  // ex.: estudos anteriores ao lançamento da área
  const pastMissing = useMemo(() => {
    const known = new Set(data.history.map((h) => h.session_id));
    const out = [];
    const base = toDate(todayISO);
    for (let i = 1; i <= 60 && out.length < 10; i++) {
      const d = new Date(base.getTime() - i * 86400000);
      const wd = d.getDay(); // 2=terça, 4=quinta
      const series = wd === 2 ? "terca" : wd === 4 ? "quinta" : null;
      if (!series) continue;
      const iso = isoOf(d);
      if (!known.has(`${series}-${iso}`)) out.push({ series, date: iso });
    }
    return out;
  }, [data.history, todayISO]);

  const pastSession = useMemo(() => {
    if (!pastSel) return null;
    const m = pastSel.match(/^(terca|quinta)-(\d{4}-\d{2}-\d{2})$/);
    if (!m) return null;
    return {
      session_id: pastSel, series: m[1], date: m[2], time: "19:00", status: "open",
      assignee_email: null, assignee_name: null, topic: "", continuity: null,
    };
  }, [pastSel]);

  return (
    <section data-testid="studies-board">
      <p className="font-sans uppercase tracking-[0.22em] text-[11px] font-semibold text-primary flex items-center gap-2">
        <BookOpen size={14} strokeWidth={1.5} /> Escala dos Estudos
      </p>
      <h2 className="mt-2 font-serif text-2xl md:text-3xl">Próximos encontros, responsáveis e continuidade.</h2>
      {twoWeeks > 0 && (
        <p className="mt-2 font-sans text-sm text-red-700 dark:text-red-300" data-testid="studies-open-count">
          {twoWeeks} {twoWeeks === 1 ? "evento sem responsável" : "eventos sem responsável"} nas próximas 2 semanas
        </p>
      )}

      <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(300px,360px)_1fr] lg:gap-10">
        <div>
          <div className="rounded-xl border border-border bg-card p-4 flex justify-center" data-testid="studies-calendar">
            <Calendar
              mode="single"
              locale={ptBR}
              className="w-fit"
              selected={selectedDate ? toDate(selectedDate) : undefined}
              month={month || undefined}
              onMonthChange={handleMonthChange}
              onSelect={(d) => {
                if (!d) return;
                const iso = isoOf(d);
                if (!byDate[iso]) return;
                setSelectedDate(iso);
                if (window.innerWidth < 1024) {
                  setTimeout(() => document.querySelector('[data-testid="study-detail"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
                }
              }}
              disabled={(d) => !byDate[isoOf(d)]}
              modifiers={modifiers}
              modifiersClassNames={{
                open: "bg-red-500/10 text-red-700 dark:text-red-300 font-semibold border border-red-500/50",
                partial: "bg-amber-500/15 text-amber-700 dark:text-amber-300 font-semibold border border-amber-500/50",
                confirmed: "bg-primary text-primary-foreground font-semibold",
                done: "bg-primary/15 text-primary font-semibold border border-primary/40",
                deactivated: "text-muted-foreground opacity-60",
                past: "bg-primary/10 text-primary/80 border border-primary/25",
                pastPending: "bg-primary/10 text-primary/80 border border-primary/25",
                info: "border border-primary/40 text-primary font-medium",
              }}
              components={{ DayContent }}
              classNames={{
                caption_label: "text-sm font-medium capitalize",
                day_selected: "ring-2 ring-primary ring-offset-2 ring-offset-background",
                day_today: "underline underline-offset-4",
              }}
            />
          </div>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 font-sans text-xs text-muted-foreground" data-testid="calendar-legend">
            <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full border border-red-500/60 bg-red-500/15" /> Pendente</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full border border-amber-500/60 bg-amber-500/20" /> Pendência parcial</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-primary" /> Confirmado</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full border border-primary/25 bg-primary/10" /> Passado</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full border border-border bg-secondary/60" /> Desativado</span>
            <span className="inline-flex items-center gap-1.5"><span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-amber-500 text-[9px] font-bold leading-none text-white">!</span> Pendência em data passada</span>
          </div>

          <div className="mt-4">
            <button
              onClick={() => setAddOpen((v) => !v)}
              data-testid="add-event-toggle"
              className="inline-flex items-center gap-2 rounded-full border border-dashed border-primary/50 px-4 py-2 font-sans text-sm text-primary hover:bg-primary/5 transition-colors"
            >
              <Plus size={14} strokeWidth={1.5} /> Adicionar evento
            </button>
            {addOpen && (
              <div className="mt-3 rounded-lg border border-border bg-card p-4 space-y-3" data-testid="add-event-form">
                <p className="font-sans text-sm text-muted-foreground">
                  Palestra com convidado, estudo extra, aniversário da equipe ou outro encontro — em qualquer data.
                  Palestras e eventos podem ser publicados no site (botão no cartão); estudos, mediúnica e aniversários ficam só aqui na intranet.
                </p>
                <div className="flex gap-2 flex-wrap">
                  <input type="date" value={addForm.date} onChange={(e) => setAddForm({ ...addForm, date: e.target.value })} className={inputCls + " flex-1 min-w-[150px]"} data-testid="add-event-date" />
                  <select value={addForm.kind} onChange={(e) => setAddForm({ ...addForm, kind: e.target.value })} className={inputCls + " flex-1 min-w-[150px]"} data-testid="add-event-kind">
                    {KIND_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                {addForm.kind === "aniversario" && (
                  <p className="font-sans text-xs text-muted-foreground" data-testid="add-event-bday-hint">
                    Acima, informe a <strong>data de nascimento</strong>: o aniversário aparece todo ano no calendário, sem precisar cadastrar de novo.
                  </p>
                )}
                {(addForm.kind === "palestra" || addForm.kind === "outro" || addForm.kind === "aniversario") && (
                  <input
                    value={addForm.title}
                    onChange={(e) => setAddForm({ ...addForm, title: e.target.value })}
                    placeholder={addForm.kind === "palestra" ? "Tema ou palestrante…" : addForm.kind === "aniversario" ? "Nome do aniversariante…" : "Descrição do evento…"}
                    className={inputCls}
                    data-testid="add-event-title"
                  />
                )}
                {addForm.kind !== "aniversario" && addForm.kind !== "mediunica" && (
                  <label className="block">
                    <span className="font-sans text-xs uppercase tracking-[0.12em] text-muted-foreground">Formato</span>
                    <select value={addForm.fmt} onChange={(e) => setAddForm({ ...addForm, fmt: e.target.value })} className={inputCls + " mt-1"} data-testid="add-event-format">
                      <option value="onsite">Presencial</option>
                      <option value="online">Online</option>
                      <option value="hybrid">Presencial + online</option>
                    </select>
                  </label>
                )}
                {addForm.kind !== "aniversario" && addForm.kind !== "mediunica" && addForm.fmt !== "onsite" && (
                  <input
                    value={addForm.online_link}
                    onChange={(e) => setAddForm({ ...addForm, online_link: e.target.value })}
                    placeholder="Link da sala online (opcional, https://…)"
                    className={inputCls}
                    data-testid="add-event-online-link"
                  />
                )}
                {addForm.kind !== "aniversario" && (
                  <label className="block">
                    <span className="font-sans text-xs uppercase tracking-[0.12em] text-muted-foreground">Horário (opcional)</span>
                    <input type="time" value={addForm.time} onChange={(e) => setAddForm({ ...addForm, time: e.target.value })} className={inputCls + " mt-1 max-w-[140px]"} data-testid="add-event-time" />
                  </label>
                )}
                <button onClick={submitCustom} className="w-full min-h-[48px] rounded-full bg-primary px-5 py-3 font-sans text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors" data-testid="add-event-save">
                  Adicionar
                </button>
              </div>
            )}
          </div>
        </div>

        <div data-testid="study-detail">
          {selectedList.length > 0 ? (
            <ul className="space-y-4">
              {selectedList.map((s) => (
                <SessionCard key={s.session_id} s={s} user={user} team={team} todayISO={todayISO} onAction={onAction} />
              ))}
            </ul>
          ) : (
            <p className="rounded-xl border border-dashed border-border p-8 text-center font-sans text-muted-foreground" data-testid="study-detail-empty">
              Escolha uma data no calendário para ver os detalhes.
            </p>
          )}
        </div>
      </div>

      <div className="mt-12">
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setHistOpen((v) => !v)}
            data-testid="history-toggle"
            className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-3 font-sans text-sm text-muted-foreground hover:text-foreground hover:border-primary transition-colors"
          >
            <History size={15} strokeWidth={1.5} />
            {histOpen ? "Esconder histórico" : "Ver histórico de eventos"}
          </button>
          <button
            onClick={toggleBdays}
            data-testid="bday-list-toggle"
            className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-3 font-sans text-sm text-muted-foreground hover:text-foreground hover:border-primary transition-colors"
          >
            <Cake size={15} strokeWidth={1.5} />
            {bdayOpen ? "Esconder aniversariantes" : "Aniversariantes da equipe"}
          </button>
        </div>

        {bdayOpen && (
          <ul className="mt-6 space-y-2" data-testid="bday-list">
            {bdayList.length === 0 && (
              <p className="font-sans text-sm text-muted-foreground">
                Nenhum aniversário cadastrado — adicione em "Adicionar evento".
              </p>
            )}
            {bdayList.map((b) => {
              const [by, bm, bd] = b.birth_date.split("-");
              const [, nm, nd] = b.next_date.split("-");
              const canDel = user.is_admin || b.created_by === user.email;
              return (
                <li key={b.birthday_id} className="flex items-center justify-between gap-3 flex-wrap rounded-xl border border-border bg-card px-5 py-3.5" data-testid={`bday-item-${b.birthday_id}`}>
                  <div>
                    <p className="font-sans text-base font-medium text-foreground">{b.name}</p>
                    <p className="font-sans text-sm text-muted-foreground">
                      {bd}/{bm}/{by} · faz {b.turning_age} anos em {nd}/{nm}
                    </p>
                  </div>
                  {canDel && (
                    bdayConfirm === b.birthday_id ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="font-sans text-sm text-muted-foreground">Excluir de todos os anos?</span>
                        <button onClick={() => delBdayFromList(b.birthday_id)} className="rounded-full bg-red-600 px-4 py-2 font-sans text-sm font-semibold text-white" data-testid="bday-item-delete-yes">Sim</button>
                        <button onClick={() => setBdayConfirm("")} className="rounded-full border border-border px-4 py-2 font-sans text-sm text-muted-foreground hover:text-foreground transition-colors">Não</button>
                      </span>
                    ) : (
                      <button onClick={() => setBdayConfirm(b.birthday_id)} className="rounded-full border border-border px-4 py-2 font-sans text-sm text-muted-foreground hover:text-red-600 hover:border-red-300 transition-colors" data-testid="bday-item-delete">Excluir</button>
                    )
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {histOpen && (
          <div className="mt-6" data-testid="history-panel">
            {pastMissing.length > 0 && (
              <div className="mb-6 rounded-lg border border-dashed border-primary/40 bg-primary/5 p-4" data-testid="history-past-box">
                <p className="font-sans text-sm text-muted-foreground">
                  Faltou registrar uma data que não está na lista? Escolha aqui e registre:
                </p>
                <select
                  value={pastSel}
                  onChange={(e) => setPastSel(e.target.value)}
                  className={inputCls + " mt-2 max-w-[340px]"}
                  data-testid="history-past-select"
                >
                  <option value="">Escolha a data…</option>
                  {pastMissing.map((p) => (
                    <option key={`${p.series}-${p.date}`} value={`${p.series}-${p.date}`}>
                      {p.series === "terca" ? "Terça-feira" : "Quinta-feira"} · {fmtShort(p.date)}
                    </option>
                  ))}
                </select>
                {pastSession && (
                  <ul className="mt-4">
                    <SessionCard s={pastSession} user={user} team={team} todayISO={todayISO} onAction={onAction} />
                  </ul>
                )}
              </div>
            )}
            <div className="flex gap-3 flex-wrap">
              <select value={histFilter.series} onChange={(e) => setHistFilter({ ...histFilter, series: e.target.value })} className={inputCls + " max-w-[220px]"} data-testid="history-filter-series">
                <option value="">Todas as séries</option>
                <option value="terca">Só terças</option>
                <option value="quinta">Só quintas</option>
                <option value="oracao">Reuniões de oração</option>
                <option value="mediunica">Reuniões mediúnicas</option>
                <option value="palestra">Palestras</option>
                <option value="estudo">Estudos extras</option>
                <option value="outro">Outros eventos</option>
              </select>
              <input value={histFilter.q} onChange={(e) => setHistFilter({ ...histFilter, q: e.target.value })} placeholder="Buscar por tema, responsável…" className={inputCls + " flex-1 min-w-[220px]"} data-testid="history-filter-q" />
            </div>
            <ul className="mt-5 divide-y divide-border/60">
              {history.map((h) =>
                h.status === "open" || h.status === "assigned" ? (
                  <SessionCard key={h.session_id} s={h} user={user} team={team} todayISO={todayISO} onAction={onAction} />
                ) : (
                  <li key={h.session_id} className="py-4" data-testid={`history-${h.session_id}`}>
                    <p className="font-sans text-sm text-muted-foreground">
                      {fmtShort(h.date)} · {SERIES_SHORT[h.series] || h.series} ·{" "}
                      {h.status === "done" ? (
                        <>Realizado por <strong className="text-foreground">{disp(h.led_by_name || h.assignee_name, h.led_by_email || h.assignee_email) || "—"}</strong></>
                      ) : (
                        <>{disp(h.assignee_name, h.assignee_email) || "—"} · <span className={h.status === "cancelled" ? "text-muted-foreground" : ""}>{STATUS_LABEL[h.status]}</span></>
                      )}
                    </p>
                    {(h.studied_topic || h.topic) && <p className="mt-1 font-sans text-base">{h.studied_topic || h.topic}</p>}
                    {h.stopped_at && <p className="font-sans text-sm text-muted-foreground">Parou em: {h.stopped_at}</p>}
                    {h.note_for_next && <p className="font-sans text-sm italic text-muted-foreground">“{h.note_for_next}”</p>}
                    {h.registered_by_name && h.registered_by_name !== (h.led_by_name || h.assignee_name) && (
                      <p className="font-sans text-xs text-muted-foreground">anotado por {h.registered_by_name}</p>
                    )}
                    {h.status === "cancelled" && h.cancel_reason && <p className="font-sans text-sm text-muted-foreground">Motivo: {h.cancel_reason}</p>}
                  </li>
                )
              )}
              {history.length === 0 && (
                <li className="py-10 text-center font-sans text-muted-foreground" data-testid="history-empty">Nenhum estudo no histórico ainda.</li>
              )}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
};
